import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { AuthContext } from '../../../context/AuthContext';
import { EVENT_NAMES } from '../../../utils/eventLogging/eventNames';
import { useTranslation } from '../../../i18n/i18n';
import { useInstruction } from '../../../hooks/recipes/useInstruction';
import ModalShell from '../../../components/ui/ModalShell';
import { Loader2 } from 'lucide-react';
import useOnlineStatus from '../../../hooks/useOnlineStatus';

function getOpenedKey(userName, instructionId) {
  return `${userName || 'unknown'}|${instructionId}`;
}

/**
 * Instruction details + acknowledgement modal:
 * - shows unconfirmed instructions from productionEvents for the active operator,
 * - marks instructions as confirmed when operator presses the Confirm button.
 */
export default function ProductionInstructionModal({
  currentRunningProduction,
  productionEvents = [],
  shiftInfo,
  settings,
  forcedInstruction,
  onClose,
}) {
  const { currentUser, logEventCustom } = useContext(AuthContext);
  const { t } = useTranslation(currentUser?.info?.languageCode);
  const isOnline = useOnlineStatus();
  const [activeInstruction, setActiveInstruction] = useState(null);
  const [isConfirming, setIsConfirming] = useState(false);
  const [confirmError, setConfirmError] = useState(false);
  const openedInstructionIdsRef = useRef(new Set());

  const { activeUserName, formatInstructionDate, markInstructionConfirmed } = useInstruction({
    currentUser,
    shiftInfo,
    settings,
    fallbackUserLabel: t('common_user'),
  });

  const currentProductionInstructions = useMemo(() => {
    if (!currentRunningProduction?.id) return [];
    return productionEvents.filter(
      (e) => e?.type === 'production_instruction' && e?.production_id === currentRunningProduction.id,
    );
  }, [productionEvents, currentRunningProduction?.id]);

  useEffect(() => {
    // Operator switch should reset currently shown instruction context.
    setActiveInstruction(null);
  }, [activeUserName]);

  useEffect(() => {
    if (!forcedInstruction) return;

    // Forced instruction path is used when opened from production details.
    // Always allowed (online or offline) — offline just renders a Close button instead of Confirm.
    openedInstructionIdsRef.current.add(getOpenedKey(activeUserName, forcedInstruction.id));
    setActiveInstruction({ instruction: forcedInstruction, instructionId: forcedInstruction.id });
  }, [forcedInstruction, activeUserName]);

  useEffect(() => {
    if (forcedInstruction) return;
    // Confirmation requires runTransaction, which doesn't work offline — don't auto-open while offline.
    if (!isOnline) return;
    if (!currentProductionInstructions.length || activeInstruction) return;

    // Auto-pick first unconfirmed instruction not yet shown to this operator.
    const nextUnconfirmed = currentProductionInstructions.find((instruction) => {
      const confirmedBy = Array.isArray(instruction?.confirmed_by) ? instruction.confirmed_by : [];
      if (confirmedBy.includes(activeUserName)) return false;
      return !openedInstructionIdsRef.current.has(getOpenedKey(activeUserName, instruction.id));
    });

    if (!nextUnconfirmed) return;

    openedInstructionIdsRef.current.add(getOpenedKey(activeUserName, nextUnconfirmed.id));
    setActiveInstruction({ instruction: nextUnconfirmed, instructionId: nextUnconfirmed.id });
  }, [activeInstruction, activeUserName, currentProductionInstructions, forcedInstruction, isOnline]);

  useEffect(() => {
    if (!activeInstruction?.instructionId || !currentProductionInstructions.length) return;

    // Keep modal content in sync with real-time updates from productionEvents.
    const updatedInstruction = currentProductionInstructions.find((e) => e.id === activeInstruction.instructionId);
    if (!updatedInstruction) return;

    setActiveInstruction((prev) => {
      if (!prev) return prev;
      return { ...prev, instruction: updatedInstruction };
    });
  }, [activeInstruction?.instructionId, currentProductionInstructions]);

  function handleCloseModal() {
    setActiveInstruction(null);
    if (onClose) onClose();
  }

  function handleOfflineClose() {
    // Allow the user to dismiss the modal while offline. The id is removed from
    // the "already opened" ref so auto-pick can re-open it once the app is online.
    // onClose is invoked so the parent resets its forced-instruction state — this
    // lets the user re-open the modal manually while still offline (otherwise the
    // parent state would stay "open" and the next click would be a no-op).
    if (activeInstruction?.instructionId) {
      openedInstructionIdsRef.current.delete(getOpenedKey(activeUserName, activeInstruction.instructionId));
    }
    setActiveInstruction(null);
    if (onClose) onClose();
  }

  async function handleConfirm() {
    flushSync(() => {
      setIsConfirming(true);
      setConfirmError(false);
    });

    try {
      await markInstructionConfirmed(activeInstruction.instruction.id, activeInstruction.instruction);

      // Only once the confirmation is durably written, so a failed confirm is never counted.
      logEventCustom(EVENT_NAMES.INSTRUCTION_CONFIRM, {
        production_id: activeInstruction.instruction.production_id || null,
        instruction_id: activeInstruction.instruction.id || null,
      });

      setIsConfirming(false);
      handleCloseModal();
    } catch {
      setIsConfirming(false);
      setConfirmError(true);
    }
  }

  if (!activeInstruction) return null;

  const isAlreadyConfirmed =
    Array.isArray(activeInstruction.instruction.confirmed_by) &&
    activeInstruction.instruction.confirmed_by.includes(activeUserName);

  return (
    <ModalShell
      title={t('production_instruction_modal_title')}
      onClose={isAlreadyConfirmed ? handleCloseModal : !isOnline ? handleOfflineClose : undefined}
      showClose={(isAlreadyConfirmed || !isOnline) && !isConfirming}
      closeOnBackdrop={(isAlreadyConfirmed || !isOnline) && !isConfirming}
      maxWidth='34.375rem'
    >
      <div className='flex gap-4'>
        <div className='flex flex-col gap-1 flex-1'>
          <span className='text-sm leading-normal font-medium text-[#8A8C95] dark:text-[#CAD5E2]'>
            {t('production_instruction_created')}
          </span>
          <div className='text-base leading-[140%] font-medium text-[#2D3041] dark:text-[#FAFAFA]'>
            {formatInstructionDate(activeInstruction.instruction.created_at) || t('common_not_available')}
          </div>
        </div>
        <div className='flex flex-col gap-1 flex-1'>
          <span className='text-sm leading-normal font-medium text-[#8A8C95] dark:text-[#CAD5E2]'>
            {t('production_instruction_manager')}
          </span>
          <div className='text-base leading-[140%] font-medium text-[#2D3041] dark:text-[#FAFAFA]'>
            {activeInstruction.instruction.user_name || t('common_unknown')}
          </div>
        </div>
      </div>
      <div className='flex flex-col gap-1 p-4 rounded-lg bg-[#F6F6F7] dark:bg-[#0F172A]'>
        <span className='text-sm leading-normal font-medium text-[#8A8C95] dark:text-[#CAD5E2]'>
          {t('production_instruction_label')}
        </span>
        <div className='text-base leading-[140%] font-medium text-[#2D3041] dark:text-[#FAFAFA]'>
          {activeInstruction.instruction.instruction}
        </div>
      </div>
      <div className='w-full flex flex-col items-end gap-2'>
        {confirmError && <p className='text-sm text-[#E04B4B]'>{t('production_instruction_confirm_failed')}</p>}
        <div className='flex gap-3'>
          {!isOnline && !isAlreadyConfirmed && (
            <button
              type='button'
              className='rounded-md px-3 py-1.5 text-base font-medium bg-light-button dark:bg-dark-button text-light-button-text dark:text-dark-button-text border border-light-button-border dark:border-dark-button-border'
              onClick={handleOfflineClose}
            >
              {t('common_close')}
            </button>
          )}
          {isAlreadyConfirmed && (
            <button
              type='button'
              className='rounded-md px-3 py-1.5 text-base font-medium bg-light-button dark:bg-dark-button text-light-button-text dark:text-dark-button-text border border-light-button-border dark:border-dark-button-border'
              onClick={handleCloseModal}
            >
              {t('common_close')}
            </button>
          )}
          {isOnline && !isAlreadyConfirmed && (
            <button
              type='button'
              disabled={isConfirming}
              aria-busy={isConfirming}
              className='flex items-center justify-center gap-2 min-w-[6.5rem] text-lg leading-[120%] font-medium text-[#FFFFFF] dark:text-[#FAFAFA] rounded-lg bg-[#2563EB] hover:bg-[#1E4FBB] active:bg-[#113C91] dark:bg-[#2773B7] dark:hover:bg-[#1D649C] dark:active:bg-[#2773B7] disabled:opacity-70 disabled:cursor-not-allowed py-2 px-4 h-11 box-border transition-all duration-300'
              onClick={handleConfirm}
            >
              {isConfirming ? <Loader2 className='size-5 animate-spin' /> : t('common_confirm')}
            </button>
          )}
        </div>
      </div>
    </ModalShell>
  );
}
